import businessSettings from "img/screenshots/epr_business_settings.png";
import payments from "img/screenshots/epr_payments.png";
import registrationNumbers from "img/screenshots/epr_settings_numbers.png";
import transactions from "img/screenshots/epr_transactions.png";
import reportingCalendar from "img/screenshots/epr_reporting_calendar.png";
import declarations from "img/screenshots/epr_declarations.png";
import helpCenter from "img/screenshots/epr_country_guides.png";

export const EPR_START_GUIDE_STEP_IDS = {
  BUSINESS: "business",
  PAYMENTS: "payments",
  REGISTRATION: "registration",
  DATA: "data",
  CALENDAR: "calendar",
  DECLARATION: "declaration",
  HELP: "help",
};

export const eprStartGuideContent = {
  texts: {
    subtitle:
      "The top left corner should say L'EPR. If it does not, click the 9-dot icon in the top right and select L'EPR.",
    allDone: "You've completed the L'EPR setup tasks.",
    showPreviousTasks: "Show previous tasks",
    tasksLeft: ({ tasksLeft, minutesLeft }) =>
      `${tasksLeft} tasks left (About ${minutesLeft} minutes)`,
    goal: "Use this guide to get ready for your first EPR declaration.",
    footerLinks: [
      { label: "Open Help Center", to: "/help" },
      { label: "View reporting calendar", to: "/report-calendar" },
    ],
    nextActions: [
      {
        title: "Review EPR declarations",
        description: "Open a draft or start the next declaration from the reporting calendar.",
        label: "Go to declarations",
        to: "/epr-reporting",
        variant: "primary",
      },
      {
        title: "Check document requests",
        description: "Eco-operator requests appear in Messages.",
        label: "Open Messages",
        to: "/messages",
      },
      {
        title: "See upcoming deadlines",
        description: "The reporting calendar lists what is due and when.",
        label: "Open calendar",
        to: "/report-calendar",
      },
    ],
  },
  minutesPerTask: 5,
  defaultSteps: [
    {
      id: EPR_START_GUIDE_STEP_IDS.BUSINESS,
      step: EPR_START_GUIDE_STEP_IDS.BUSINESS,
      name: "Business details",
      category: "required",
      complete: false,
    },
    {
      id: EPR_START_GUIDE_STEP_IDS.PAYMENTS,
      step: EPR_START_GUIDE_STEP_IDS.PAYMENTS,
      name: "Payment details",
      category: "required",
      complete: false,
    },
    {
      id: EPR_START_GUIDE_STEP_IDS.REGISTRATION,
      step: EPR_START_GUIDE_STEP_IDS.REGISTRATION,
      name: "EPR registration numbers",
      category: "recommended",
      optional: true,
      complete: false,
    },
    {
      id: EPR_START_GUIDE_STEP_IDS.DATA,
      step: EPR_START_GUIDE_STEP_IDS.DATA,
      name: "Add EPR data",
      category: "recommended",
      optional: true,
      complete: false,
    },
    {
      id: EPR_START_GUIDE_STEP_IDS.CALENDAR,
      step: EPR_START_GUIDE_STEP_IDS.CALENDAR,
      name: "Reporting calendar",
      category: "learn",
      complete: false,
    },
    {
      id: EPR_START_GUIDE_STEP_IDS.DECLARATION,
      step: EPR_START_GUIDE_STEP_IDS.DECLARATION,
      name: "File an EPR declaration",
      category: "optional",
      optional: true,
      requires: [EPR_START_GUIDE_STEP_IDS.REGISTRATION],
      lockReason:
        "This task opens after you add an active EPR registration number. Declarations are filed per country and number.",
      complete: false,
    },
    {
      id: EPR_START_GUIDE_STEP_IDS.HELP,
      step: EPR_START_GUIDE_STEP_IDS.HELP,
      name: "Help",
      category: "learn",
      optional: true,
      complete: false,
    },
  ],
  steps: {
    [EPR_START_GUIDE_STEP_IDS.BUSINESS]: {
      name: "Business details",
      description:
        "Open Business settings, then Business. Enter the company details in English and without special characters. These details are used for filing declarations.",
      to: "/business-settings?tab=business",
      actionLabel: "Fill",
      image: businessSettings,
    },
    [EPR_START_GUIDE_STEP_IDS.PAYMENTS]: {
      name: "Payment details",
      description:
        "In Payments, specify the company that pays for the subscription and receives invoices, and select a currency. UK paying companies are billed in pounds sterling.",
      to: "/product-settings/payments",
      actionLabel: "Fill",
      image: payments,
    },
    [EPR_START_GUIDE_STEP_IDS.REGISTRATION]: {
      name: "EPR registration numbers",
      description:
        "Use the Registration tracker to follow registrations handled by Lappa. If you already have an EPR number, add it in EPR settings. Activation requests and required documents appear in Messages.",
      to: "/settings",
      actionLabel: "Fill",
      image: registrationNumbers,
      secondaryActions: [
        { label: "Track a Lappa registration", to: "/settings" },
      ],
      learnMore: { label: "Learn more", to: "/help" },
    },
    [EPR_START_GUIDE_STEP_IDS.DATA]: {
      name: "Add EPR data",
      description:
        "Add reporting data through an integration, an Excel upload, Packaging SKU, or directly in a declaration. Transactions is the best place to review imported and uploaded data.",
      to: "/transactions",
      actionLabel: "Fill",
      image: transactions,
      learnMore: { label: "Learn more", to: "/help" },
    },
    [EPR_START_GUIDE_STEP_IDS.CALENDAR]: {
      name: "Reporting calendar",
      description:
        "When your EPR numbers are active, use the reporting calendar to see which declarations are due and when. You can start a declaration from a calendar deadline.",
      to: "/report-calendar",
      actionLabel: "Open reporting calendar",
      image: reportingCalendar,
    },
    [EPR_START_GUIDE_STEP_IDS.DECLARATION]: {
      name: "File an EPR declaration",
      description:
        "Open EPR declarations and click + Fill EPR declaration. Select the country and reporting period, check any pre-filled data, complete the country-specific steps, and submit when ready.",
      to: "/epr-reporting",
      actionLabel: "Fill",
      image: declarations,
    },
    [EPR_START_GUIDE_STEP_IDS.HELP]: {
      name: "Help",
      description:
        "Use Messages for requests from eco-operators. Open the Help Center for country guides and support, or use live chat during support hours.",
      to: "/help",
      actionLabel: "Open Help Center",
      image: helpCenter,
    },
  },
};
